import React, { FC, useState, useRef } from 'react';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Checkbox,
  Box,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

interface ColumnOrderModalProps {
  open: boolean;
  onClose: () => void;
  columns: GridColDef[];
  columnOrder: string[];
  hiddenColumns: string[];
  onSavePreferences: (preferences: { columnOrder: string[]; hiddenColumns: string[] }) => void;
}

const ColumnOrderModal: FC<ColumnOrderModalProps> = ({
  open,
  onClose,
  columns,
  columnOrder,
  hiddenColumns,
  onSavePreferences,
}) => {
  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>(columnOrder);
  const [localHiddenColumns, setLocalHiddenColumns] = useState<string[]>(hiddenColumns);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const handleReset = () => {
    const defaultOrder = columns.map((col) => col.field);
    setLocalColumnOrder(defaultOrder);
    setLocalHiddenColumns([]);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...localColumnOrder];
    const temp = newOrder[index];
    newOrder[index] = newOrder[index - 1];
    newOrder[index - 1] = temp;
    setLocalColumnOrder(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index === localColumnOrder.length - 1) return;
    const newOrder = [...localColumnOrder];
    const temp = newOrder[index];
    newOrder[index] = newOrder[index + 1];
    newOrder[index + 1] = temp;
    setLocalColumnOrder(newOrder);
  };

  const handleVisibilityToggle = (field: string) => {
    setLocalHiddenColumns((prev) => (prev.includes(field) ? prev.filter((col) => col !== field) : [...prev, field]));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add semi-transparent effect to dragged item
    if (e.target instanceof HTMLElement) {
      e.target.style.opacity = '0.3';
    }
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;
    setDropTargetIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;

    const newOrder = [...localColumnOrder];
    const [draggedItem] = newOrder.splice(draggingIndex, 1);
    newOrder.splice(index, 0, draggedItem);

    setLocalColumnOrder(newOrder);
    setDraggingIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.target instanceof HTMLElement) {
      e.target.style.opacity = '';
    }
    setDraggingIndex(null);
    setDropTargetIndex(null);
  };

  const getColumnName = (field: string) => {
    const column = columns.find((col) => col.field === field);
    return column?.headerName || field;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={500}>
            Configurar Columnas
          </Typography>
          <Button
            onClick={handleReset}
            size="small"
            sx={{
              textTransform: 'none',
            }}
          >
            Restaurar configuración original
          </Button>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Selecciona las columnas que deseas ver y arrastra para reordenarlas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Usa los checkboxes para mostrar u ocultar columnas
          </Typography>
        </Box>
        <List
          ref={listRef}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 0,
          }}
        >
          {localColumnOrder.map((field, index) => (
            <ListItem
              key={field}
              dense
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'grab',
                '&:active': {
                  cursor: 'grabbing',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  borderTop:
                    index === dropTargetIndex && draggingIndex !== null && draggingIndex > dropTargetIndex
                      ? '2px solid black'
                      : 'none',
                  borderBottom:
                    index === dropTargetIndex && draggingIndex !== null && draggingIndex < dropTargetIndex
                      ? '2px solid black'
                      : 'none',
                }}
              >
                <DragIndicatorIcon
                  sx={{
                    color: 'text.secondary',
                    mr: 1,
                    cursor: 'grab',
                    '&:active': {
                      cursor: 'grabbing',
                    },
                  }}
                />
                <Checkbox
                  checked={!localHiddenColumns.includes(field)}
                  onChange={() => handleVisibilityToggle(field)}
                  sx={{ mr: 1 }}
                  size="small"
                />
                <ListItemText primary={getColumnName(field)} />
              </Box>
              <Box>
                <IconButton size="small" onClick={() => handleMoveUp(index)} disabled={index === 0} sx={{ mr: 1 }}>
                  <KeyboardArrowUpIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === localColumnOrder.length - 1}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={() => {
            onSavePreferences({
              columnOrder: localColumnOrder,
              hiddenColumns: localHiddenColumns,
            });
            onClose();
          }}
          variant="contained"
          color="primary"
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnOrderModal;
